import * as React from 'react'
import Select from 'react-select'
import TextField from './TextField'
import Cloud from '../icons/drivers/Cloud'
import Dropzone from 'react-dropzone'
import * as Yup from 'yup'
import { Field, Form, Formik, FormikProps } from 'formik'
import { ICreateDriver, IProviderObject, IStateDriver } from '../../types'
import EntityBottomPanel from '../ui/entity/EntityBottomPanel'
import EntityBottomPanelBtn from '../ui/entity/EntityBottomPanelBtn'
import PreloaderCircle from '../ui/PreloaderCircle'
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input'
import * as classNames from 'classnames'
import UIMessages from 'src/utils/ui-messages'
import moment, { Moment } from 'moment'
import { Datepicker, MbscCalendarEvent } from '@mobiscroll/react'
import FloatingForm from '../ui/form/FloatingForm'
import FormLabel from '../ui/form/FormLabel'

export interface IOperatorInfoFormProps {
  handleSubmit: (data: ICreateDriver) => Promise<any>
  driver?: IStateDriver
  history: any
  match: any
  submitBtnText: string
  loading: boolean
}

export interface IOperatorInfoFormState {
  isSaving: boolean
}

const handleDrop = (acceptedFiles: any) => {
  acceptedFiles.map((file: any) => {
    const reader = new FileReader()
    reader.onload = () => {
      const fileAsBinaryString = reader.result
    }
    reader.onabort = () => console.log('file reading was aborted')
    reader.onerror = () => console.log('file reading has failed')
    reader.readAsBinaryString(file)
  })
}

class OperatorInfoForm extends React.Component<IOperatorInfoFormProps, IOperatorInfoFormState> {
  public state = {
    isSaving: false
  }

  private infoFormRef: any

  public render() {
    const driver = this.props.driver || {
      user: {
        username: '',
        email: { emailAddress: '' },
        firstName: '',
        lastName: '',
        phones: [{ phoneNumber: '' }],
        driversLicense: '',
        birthDate: '',
        street: '',
        city: '',
        state: '',
        zip: '',
        country: ''
      }
    }

    const validationSchema = Yup.object().shape({
      email: Yup.string()
        .email('Invalid email address')
        .required('This field is required '),
      username: Yup.string().required('This field is required'),
      firstName: Yup.string().required('This field is required'),
      lastName: Yup.string().required('This field is required'),
      phone: Yup.string()
        .test(
          'phone-validation',
          UIMessages.driver.validation.phoneNumberInvalid,
          value => !value || isValidPhoneNumber(value)
        )
        .required('This field is required'),
      birthDate: Yup.date().required('This field is required')
    })

    const formikRender = (formikProps: FormikProps<IFormValues>) => (
      <OperatorInfoFormFields {...formikProps} submitBtnText={this.props.submitBtnText} />
    )

    const email = driver.user.email ? driver.user.email.emailAddress : ''
    return (
      <div className='col-12'>
        <Formik
          ref={this.formRef}
          initialValues={{
            email,
            username: driver.user.username,
            firstName: driver.user.firstName,
            lastName: driver.user.lastName,
            phone: driver.user.phones && driver.user.phones.length > 0 ? driver.user.phones[0].phoneNumber : '',
            driversLicense:
              driver.user.driversLicense && driver.user.driversLicense.length > 0 ? driver.user.driversLicense : '',
            birthDate: undefined,
            street: '',
            city: '',
            state: '',
            zip: '',
            country: ''
          }}
          validationSchema={validationSchema}
          onSubmit={this.saveInfo}
          render={formikRender}
        />
        <div className='absolute bottom-0 left-0 right-0'>
          <EntityBottomPanel>
            <div className='flex justify-between'>
              <div />
              <div className='flex justify-end'>
                {this.state.isSaving ? (
                  <PreloaderCircle loading={true} />
                ) : (
                  <EntityBottomPanelBtn onClick={this.submitForm} isActive={!this.state.isSaving}>
                    Save
                  </EntityBottomPanelBtn>
                )}
              </div>
            </div>
          </EntityBottomPanel>
        </div>
      </div>
    )
  }

  private saveInfo = (info: ICreateDriver) => {
    this.setState({ isSaving: true })
    this.props.handleSubmit({ ...info }).then(() => {
      this.setState({ isSaving: false })
    })
  }

  private submitForm = () => {
    this.infoFormRef.submitForm()
  }

  private formRef = (node: any) => {
    this.infoFormRef = node
  }
}

interface IFormValues {
  firstName: string
  lastName: string
  phone?: string
  email: string
  username: string
  driversLicense?: string
  birthDate: Date
  street: string
  city: string
  state: string
  zip: string
  country: string
}

interface IOperatorInfoFormFieldsProps extends FormikProps<IFormValues> {
  submitBtnText: string
}

const OperatorInfoFormFields = (props: IOperatorInfoFormFieldsProps) => {
  const { errors, touched, handleSubmit, values, setFieldValue, setFieldTouched, handleBlur } = props

  const handlePhoneField = (value: any) => {
    setFieldTouched('phone', true)
    if (!isValidPhoneNumber(value)) {
      setFieldValue('phone', value)
      props.errors.phone = UIMessages.rider.validation.phoneNumberInvalid + ' (' + value + ')'
    } else {
      setFieldValue('phone', value)
      props.errors.phone = undefined
    }
  }

  const handleBirthdayChange = (date: MbscCalendarEvent) => {
    setFieldTouched('birthDate', true)
    setFieldValue('birthDate', date ? moment(date.value).format('L') : undefined)
  }

  return (
    <Form>
      <FloatingForm fullHeight={true}>
        <>
          <div className='form-modal-section full-section'>
            <div className='col-8'>
              <Dropzone
                accept='image/png'
                className='flex items-center dropFile p2 cursor--pointer'
                onDrop={handleDrop}
              >
                <Cloud className='relative' />
                <div className='ml2'>
                  <div className='font-green h6'>Click to browse</div>
                  <div className='font-light-blue-grey h6'>or Drag and Drop image here</div>
                </div>
              </Dropzone>
            </div>
            <div className='col-8 pt2'>
              <div className='flex'>
                <div className='col-6 mb2 mr2'>
                  <TextField
                    handleBlur={props.handleBlur}
                    title='First Name'
                    placeholder='First Name'
                    error={props.touched.firstName && props.errors.firstName}
                    onChange={props.handleChange}
                    name={'firstName'}
                    value={props.values.firstName}
                  />
                  {props.touched.firstName && props.errors.firstName && (
                    <span className='font-small weight-300 font-red'>{props.errors.firstName}</span>
                  )}
                </div>
                <div className='col-6 mb2'>
                  <TextField
                    handleBlur={props.handleBlur}
                    title='Last Name'
                    placeholder='Last Name'
                    error={props.touched.lastName && props.errors.lastName}
                    onChange={props.handleChange}
                    name={'lastName'}
                    value={props.values.lastName}
                  />
                  {props.touched.lastName && props.errors.lastName && (
                    <span className='font-small weight-300 font-red'>{props.errors.lastName}</span>
                  )}
                </div>
              </div>
              <div className='flex'>
                <div className='col-6 mb2 mr2'>
                  <TextField
                    handleBlur={props.handleBlur}
                    title='Username'
                    placeholder='Username'
                    error={props.touched.username && props.errors.username}
                    onChange={props.handleChange}
                    name={'username'}
                    value={props.values.username}
                  />
                  {props.touched.username && props.errors.username && (
                    <span className='font-small weight-300 font-red'>{props.errors.username}</span>
                  )}
                </div>
                <div className='col-6 mb2'>
                  <TextField
                    handleBlur={props.handleBlur}
                    title='Email'
                    placeholder='Email'
                    error={props.touched.email && props.errors.email}
                    onChange={props.handleChange}
                    name={'email'}
                    value={props.values.email}
                  />
                  {props.touched.email && props.errors.email && (
                    <span className='font-small weight-300 font-red'>{props.errors.email}</span>
                  )}
                </div>
              </div>
              <div className='flex full-width-datepicker'>
                <div className='col-6 mb2 mr2'>
                  <PhoneInput
                    className='phone-input'
                    name='phone'
                    placeholder='Phone number'
                    defaultCountry='US'
                    value={props.values.phone}
                    onChange={handlePhoneField}
                    onBlur={handleBlur}
                  />
                  {props.touched.phone && props.errors.phone && (
                    <span className='font-small weight-300 font-red'>{props.errors.phone}</span>
                  )}
                </div>
                <div className='col-6 mb2 flex flex-column'>
                  <div className='ios-datepicker h100'>
                    <div className='datepicker-label'>{`${UIMessages.common.birthday}*`}</div>
                    <div>
                      <Datepicker
                        controls={['calendar']}
                        dateFormat='MM/DD/YYYY'
                        max={moment()}
                        value={props.values.birthDate !== undefined ? moment(props.values.birthDate) : undefined}
                        onChange={handleBirthdayChange}
                        theme='ios'
                        themeVariant='light'
                        inputStyle='box'
                        labelStyle='floating'
                        touchUi={false}
                        display='anchored'
                        calendarScroll='horizontal'
                      />
                    </div>
                    {props.touched.birthDate && props.errors.birthDate && (
                      <span className='font-small weight-300 font-red'>{props.errors.birthDate}</span>
                    )}
                  </div>
                </div>
              </div>
              <div className='flex'>
                <div className='col-12'>
                  <TextField
                    handleBlur={props.handleBlur}
                    title={`Driver's License Number`}
                    placeholder={`Driver's License Number`}
                    error={props.touched.driversLicense && props.errors.driversLicense}
                    onChange={props.handleChange}
                    name={'driversLicense'}
                    value={props.values.driversLicense ? props.values.driversLicense : ''}
                  />
                  {props.touched.driversLicense && props.errors.driversLicense && (
                    <span className='font-small weight-300 font-red'>{props.errors.driversLicense}</span>
                  )}
                </div>
              </div>

              <div className='flex mt2'>
                <FormLabel>Driver Address</FormLabel>
              </div>

              <div className='bb-shadow-grey'>&nbsp;</div>

              <div className='flex flex-row mt1'>
                <div style={{ flex: 1 }} className='mt1'>
                  <TextField
                    handleBlur={props.handleBlur}
                    title='Street'
                    placeholder='Street'
                    error={props.touched.street && props.errors.street}
                    onChange={props.handleChange}
                    name={'street'}
                    value={props.values.street}
                  />
                  {props.touched.street && props.errors.street && (
                    <span className='ErrorMsgDivForLastName font-small weight-300 font-red'>{props.errors.street}</span>
                  )}
                </div>
              </div>

              <div className='flex flex-row'>
                <div style={{ flex: 1 }} className='mt1'>
                  <TextField
                    handleBlur={props.handleBlur}
                    title='City'
                    placeholder='City'
                    error={props.touched.city && props.errors.city}
                    onChange={props.handleChange}
                    name={'city'}
                    value={props.values.city}
                  />
                  {props.touched.city && props.errors.city && (
                    <span className='font-small weight-300 font-red'>{props.errors.city}</span>
                  )}
                </div>
                <div style={{ flex: 1 }} className='block ml1 mt1'>
                  <TextField
                    handleBlur={props.handleBlur}
                    title='State'
                    placeholder='State'
                    error={props.touched.state && props.errors.state}
                    onChange={props.handleChange}
                    name={'state'}
                    value={props.values.state}
                  />
                  {props.touched.state && props.errors.state && (
                    <span className='font-small weight-300 font-red'>{props.errors.state}</span>
                  )}
                </div>
              </div>

              <div className='flex flex-row'>
                <div style={{ flex: 1 }} className='mt1'>
                  <TextField
                    handleBlur={props.handleBlur}
                    title='Zip'
                    placeholder='Zip'
                    error={props.touched.zip && props.errors.zip}
                    onChange={props.handleChange}
                    name={'zip'}
                    value={props.values.zip}
                  />
                  {props.touched.zip && props.errors.zip && (
                    <span className='font-small weight-300 font-red'>{props.errors.zip}</span>
                  )}
                </div>
                <div style={{ flex: 1 }} className='block ml1 mt1'>
                  <TextField
                    handleBlur={props.handleBlur}
                    title='Country'
                    placeholder='Country'
                    error={props.touched.country && props.errors.country}
                    onChange={props.handleChange}
                    name={'country'}
                    value={props.values.country}
                  />
                  {props.touched.country && props.errors.country && (
                    <span className='font-small weight-300 font-red'>{props.errors.country}</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      </FloatingForm>
    </Form>
  )
}

export default OperatorInfoForm



// WEBPACK FOOTER //
// ./src/components/drivers/OperatorInfoForm.tsx